// store.js — single source of truth for the GCBC prototype.
//
// Hydrates from /src/data/*.json on first load, persists every mutation to
// localStorage, and broadcasts a change event so subscribed components
// re-render.
//
// Entity arrays are STABLE references — mutators splice in place so any
// importer (legacy and new) sees the same live array reference.

import membersRaw       from "@/data/members.json";
import dealsRaw         from "@/data/deals.json";
import brandsRaw        from "@/data/brands.json";
import applicationsRaw  from "@/data/applications.json";
import reservationsRaw  from "@/data/reservations.json";
import submissionsRaw   from "@/data/submissions.json";
import cardsRaw         from "@/data/cards.json";
import paymentItemsRaw  from "@/data/payment-items.json";
import paymentRunsRaw   from "@/data/payment-runs.json";
import auditEventsRaw   from "@/data/audit-events.json";
import validationRules  from "@/data/validation-rules.json";
import adminUsersRaw    from "@/data/admin-users.json";
import manifest         from "@/data/manifest.json";

import { normalizeAll } from "./dataAdapter";

const STORE_KEY = "gcbc:store:v23";
const EVENT     = "gcbc:store:change";

// Build the normalized data from the raw JSON.
function buildInitial() {
  return normalizeAll({
    members:        membersRaw,
    deals:          dealsRaw,
    brands:         brandsRaw,
    applications:   applicationsRaw,
    reservations:   reservationsRaw,
    submissions:    submissionsRaw,
    cards:          cardsRaw,
    paymentItems:   paymentItemsRaw,
    paymentRuns:    paymentRunsRaw,
    auditEvents:    auditEventsRaw,
    validationRules,
    adminUsers:     adminUsersRaw,
    manifest,
  });
}

// ─── Stable entity arrays — same reference for the life of the page ─────────
export const STORE = {
  brands:           [],
  deals:            [],
  members:          [],
  applications:     [],
  reservations:     [],
  submissions:      [],
  cards:            [],
  paymentItems:     [],
  paymentRuns:      [],
  auditEvents:      [],
  validationRules:  [],
  adminUsers:       [],
  manifest:         {},
  version:          0,
};

const ARRAY_KEYS = [
  "brands","deals","members","applications","reservations","submissions",
  "cards","paymentItems","paymentRuns","auditEvents","validationRules","adminUsers",
];

function replaceArray(target, source) {
  // Mutate-in-place so the exported reference stays stable.
  target.splice(0, target.length, ...source);
}

function hydrate(state) {
  ARRAY_KEYS.forEach(k => replaceArray(STORE[k], state[k] || []));
  STORE.manifest = state.manifest || manifest;
  STORE.version  = state.version  || 0;
  // Auto-expire pass: any Active / SubmittedPartial reservation whose deal
  // deadline has passed transitions to ExpiredReleased so the displayed
  // status is never inconsistent with the deal timeline. We do this in
  // hydrate so a long-stale localStorage cleans itself up on next boot —
  // not on every mutation (avoids audit-log spam).
  const now = Date.now();
  let changed = false;
  STORE.reservations.forEach(r => {
    if (r.status !== "Active" && r.status !== "SubmittedPartial") return;
    const deal = STORE.deals.find(d => d.id === r.dealId);
    if (!deal?.deadline) return;
    if (new Date(deal.deadline).getTime() < now) {
      r.status = "ExpiredReleased";
      r.expiredAt = r.expiredAt || new Date().toISOString();
      changed = true;
      // Release any unused face back to brand pool.
      const releaseFace = Math.max(0, (r.faceValueReserved || 0) - (r.faceValueApproved || 0));
      if (releaseFace > 0) {
        const d = STORE.deals.find(dd => dd.id === r.dealId);
        if (d?.brandConfigs) {
          d.brandConfigs = d.brandConfigs.map(c => c.brandId === r.brandId
            ? { ...c, capUsed: Math.max(0, (c.capUsed || 0) - releaseFace) }
            : c);
        }
      }
    }
  });
  if (changed) STORE.version += 1;
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.__v === 16 && Array.isArray(parsed.members)) return parsed;
    }
  } catch { /* fall through */ }
  return null;
}

function persist() {
  try {
    const snapshot = { __v: 16, version: STORE.version };
    ARRAY_KEYS.forEach(k => { snapshot[k] = STORE[k]; });
    snapshot.manifest = STORE.manifest;
    localStorage.setItem(STORE_KEY, JSON.stringify(snapshot));
  } catch { /* quota */ }
}

// One-time bootstrap.
(function bootstrap() {
  const persisted = loadFromStorage();
  if (persisted) {
    hydrate(persisted);
  } else {
    hydrate({ ...buildInitial(), version: 0 });
    persist();
  }
})();

export function getStore() {
  return STORE;
}

export function getVersion() {
  return STORE.version;
}

/**
 * mutate(updater) — updater receives a draft with array proxies you can
 * reassign (e.g., `draft.cards = draft.cards.map(...)`); we splice each
 * resulting array back into the stable reference, then persist + notify.
 */
export function mutate(updater) {
  const draft = {};
  ARRAY_KEYS.forEach(k => { draft[k] = STORE[k].slice(); });
  draft.manifest = STORE.manifest;
  updater(draft);
  ARRAY_KEYS.forEach(k => replaceArray(STORE[k], draft[k]));
  // Write back manifest too so livePin re-pinning (current-user session swap)
  // persists across renders + localStorage.
  STORE.manifest = draft.manifest;
  STORE.version += 1;
  persist();
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { version: STORE.version } })); } catch { /* SSR */ }
}

export function subscribe(listener) {
  const fn = () => listener(STORE);
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}

export function resetStore() {
  try {
    localStorage.removeItem(STORE_KEY);
    // Also clear per-component overrides (admin demo toggles) so the wipe
    // truly returns to seed state without forcing the user to sign out.
    localStorage.removeItem("gcbc:demo:v1");
  } catch { /* noop */ }
  hydrate({ ...buildInitial(), version: 0 });
  persist();
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { version: STORE.version } })); } catch { /* SSR */ }
}

// Audit event helper — call inside a `mutate` updater.
let auditCounter = 0;
export function appendAuditEvent(draft, ev) {
  const id = `aud_local_${Date.now()}_${++auditCounter}`;
  // Resolve actor name + role from actorId (admin or member). Falls back to
  // explicit overrides for legacy callers, then to the pinned super admin.
  let actorName = ev.actorName;
  let actorRole = ev.actorRole;
  if (!actorName || !actorRole) {
    const actorId = ev.actorId || draft.manifest?.adminUsers?.SUPER_ADMIN || "adm_001";
    const admin = draft.adminUsers.find(a => a.id === actorId);
    const member = !admin ? draft.members.find(m => m.id === actorId) : null;
    if (admin) {
      actorName = actorName || `${admin.firstName} ${admin.lastName}`;
      actorRole = actorRole || admin.role || "Admin";
    } else if (member) {
      actorName = actorName || `${member.firstName} ${member.lastName}`;
      actorRole = actorRole || "Member";
    } else {
      actorName = actorName || "Todd Maxwell";
      actorRole = actorRole || "Super Admin";
    }
  }
  const event = {
    id,
    at:           new Date().toISOString(),
    actorId:      ev.actorId || draft.manifest?.adminUsers?.SUPER_ADMIN || "adm_001",
    actorName,
    actorRole,
    action:       ev.action,
    entityType:   ev.entityType,
    entityId:     ev.entityId,
    description:  ev.description || ev.action,
    ipAddress:    ev.ipAddress || "207.158.42.18",
    isDecryption: !!ev.isDecryption,
  };
  draft.auditEvents = [event, ...(draft.auditEvents || [])];
  return event;
}
